import React, { useEffect, useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import analyticsService, { DailyProductionRow } from '../../services/analyticsService';
import Button from '../UI/Button';
import LoadingSpinner from '../UI/LoadingSpinner';

const FilingReport: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState<string>(today);
  const [toDate, setToDate] = useState<string>(today);
  const [rows, setRows] = useState<DailyProductionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [mealTypeFilter, setMealTypeFilter] = useState<string>('');

  const loadData = async () => {
    try {
      setLoading(true);
      // Load data for each day in the date range
      const allRows: DailyProductionRow[] = [];
      const start = new Date(fromDate);
      const end = new Date(toDate);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const res = await analyticsService.getDailyProductionAnalytics({
          serviceDate: dateStr,
        });
        // Add the date to each row
        const rowsWithDate = (res.rows || []).map(row => ({
          ...row,
          serviceDate: dateStr,
        }));
        allRows.push(...rowsWithDate);
      }

      setRows(allRows);
    } catch (error) {
      console.error('Failed to load filing data:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  // Filter rows based on meal type
  const filteredRows = rows.filter(row => {
    const matchesMealType = !mealTypeFilter || row.mealType === mealTypeFilter;
    return matchesMealType;
  });

  const uniqueClientCount = new Set(filteredRows.map((r) => r.clientId)).size;

  // Get unique meal types for filter dropdown
  const uniqueMealTypes = Array.from(new Set(rows.map(r => r.mealType))).sort();

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">KITCHEN / FILING REPORT</h2>
          <p className="text-gray-600 mt-2">
            {fromDate} to {toDate} • Total Clients: {uniqueClientCount}
          </p>
        </div>
        <div className="flex items-end space-x-2">
          <Button onClick={() => window.print()} variant="outline" className="flex items-center justify-center">
            <PrinterIcon className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-soft p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
            <select
              value={mealTypeFilter}
              onChange={(e) => setMealTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Meals</option>
              {uniqueMealTypes.map(mealType => (
                <option key={mealType} value={mealType}>{mealType}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft p-6">
          {filteredRows.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No production data for this date range.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="px-2 py-2 text-left font-bold text-gray-900">#</th>
                    <th className="px-2 py-2 text-left font-bold text-gray-900">Date</th>
                    <th className="px-2 py-2 text-left font-bold text-gray-900">Client</th>
                    <th className="px-2 py-2 text-left font-bold text-gray-900">Diet Plan</th>
                    <th className="px-2 py-2 text-left font-bold text-gray-900">Meal</th>
                    <th className="px-2 py-2 text-left font-bold text-gray-900">Ingredients</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => {
                    return (
                      <tr key={`${row.clientId}-${row.plateId}-${index}`} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-2 py-2 text-gray-600">{index + 1}</td>
                        <td className="px-2 py-2 text-gray-700">{(row as any).serviceDate}</td>
                        <td className="px-2 py-2 font-medium text-gray-900">{row.clientName}</td>
                        <td className="px-2 py-2 text-gray-700">{row.dietName || '-'}</td>
                        <td className="px-2 py-2 text-gray-700">{row.mealType}</td>
                        <td className="px-2 py-2 text-gray-700 max-w-md" title={row.ingredientsList}>
                          {row.ingredientsList}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilingReport;
